/* eslint-disable react-hooks/exhaustive-deps */
import { BufferTracker } from '@bufferapp/buffer-tracking-browser-ts';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useAccount } from '~/accountContext';
import { SlateJsProxy } from '~/composer/composer/entities/EditorStateProxy';
import { Idea, IdeaContent, isExistingIdea, NewIdea } from '~/content/types';
import { getPlainText } from '~/editor/BufferEditor';
import { addNotification } from '~/notifications-provider/notificationsSlice';
import { useAppDispatch, useAppSelector } from '~/store';
import { selectIncludedMedia } from '~/uploads/state/selectors';
import {
  markExtension,
  selectIsIdeaFromExtension,
} from '~/content/state/ideasSlice';
import { createIdeasEditor } from '~/editor/IdeasEditor';
import { BufferEditor } from '~/editor/BufferEditor/types.plate';
import { preloadMedia } from '~/uploads/state/thunks/preloadMedia';
import { IDEAS_UPLOADER_ID } from '../config';
import { useCreateIdea } from './useCreateIdea';
import { useEditIdea } from './useEditIdea';

const emptyIdea: NewIdea = {
  content: {
    text: '',
  },
};

type useIdeaEditorReturn = {
  isOpen: boolean;
  isEditMode: boolean;
  onOpenChange: (open: boolean) => void;
  openEditor: (idea?: NewIdea) => void;
  closeEditor: () => void;
  currentIdea?: NewIdea;
  saveIdea: () => void;
  getCurrentContent: () => Idea | NewIdea | undefined;
  errorCode: string;
  showErrorMessage: boolean;
  plateEditor: BufferEditor;
};

/**
 * Hook responsible rendering and managing external state of Ideas editor.
 * Includes:
 * - open state
 * - storing the current idea
 * - setting the intitial state of the editor
 * - saving ideas by serializing internal editor values to content
 */
export const useIdeasEditor = (): useIdeaEditorReturn => {
  const [isOpen, setIsOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentIdea, setCurrentIdea] = useState<NewIdea | undefined>(
    undefined
  );
  const dispatch = useAppDispatch();
  const media = useAppSelector((state) =>
    selectIncludedMedia(state, IDEAS_UPLOADER_ID)
  );
  const fromExtension = useAppSelector((state) =>
    selectIsIdeaFromExtension(state)
  );

  const [showErrorMessage, setShowErrorMessage] = useState<boolean>(false);
  const [errorCode, setErrorCode] = useState<string>('');
  const { t } = useTranslation();

  // Idea hooks
  const { createIdea, createIdeaData, createIdeaError, createIdeaLoading } =
    useCreateIdea();
  const { updateIdea, updateIdeaData, updateIdeaError, updateIdeaLoading } =
    useEditIdea();

  // Plate hooks
  const plateEditor = useMemo(() => {
    return SlateJsProxy.createStateFromText(
      createIdeasEditor(),
      currentIdea?.content?.text || ''
    );
  }, [currentIdea]);

  // Get organizationID for Tracking purposes
  const accountContext = useAccount();
  const account = accountContext?.account;
  const organizationId = account?.currentOrganization?.id;

  const openEditor = useCallback((idea: NewIdea = emptyIdea) => {
    setIsOpen(true);
    setCurrentIdea(idea);
    dispatch(
      preloadMedia({
        media: idea.content.media || [],
        uploaderId: IDEAS_UPLOADER_ID,
      })
    );

    if (organizationId) {
      BufferTracker.ideaComposerOpened({
        clientName: 'publishWeb',
        organizationId,
        ideaId: idea.id,
      });
    }
    if (idea.id) {
      setIsEditMode(true);
    } else {
      setIsEditMode(false);
    }
  }, []);

  const resetErrorsOnClose = () => {
    setShowErrorMessage(false);
    setErrorCode('');
  };

  const closeEditor = useCallback(() => {
    setShowErrorMessage(false);
    setIsOpen(false);
    setCurrentIdea(undefined);
    resetErrorsOnClose();
  }, []);

  // This is meant specifically for the Radix Dialog Root component
  const onOpenChange = useCallback(
    (open: boolean) => {
      if (open) openEditor();
      else closeEditor();
    },
    [openEditor, closeEditor]
  );

  const resetErrorMessage = useCallback(() => {
    setTimeout(() => {
      setShowErrorMessage(false);
      setErrorCode('');
    }, 5000);
  }, []);

  // Update error message if create is unsuccessful or else close the editor
  useEffect(() => {
    if (createIdeaError) {
      setShowErrorMessage(true);
      resetErrorMessage();
    } else if (createIdeaData && createIdeaData.ideaCreate?.error) {
      setShowErrorMessage(true);
      setErrorCode(createIdeaData.ideaCreate?.error);
      resetErrorMessage();
    } else if (createIdeaData) {
      setShowErrorMessage(false);
      closeEditor();
      dispatch(markExtension({ fromExtension: false }));
      dispatch(
        addNotification(
          `✅ ${t('content.notifications.createIdea.successMessage')}`
        )
      );
    }
  }, [createIdeaLoading]);

  // Update error Message if Update is unsuccessful or else close the editor
  useEffect(() => {
    if (updateIdeaError) {
      setShowErrorMessage(true);
      resetErrorMessage();
    } else if (updateIdeaData && updateIdeaData.ideaUpdate?.error) {
      setShowErrorMessage(true);
      resetErrorMessage();
    } else if (updateIdeaData) {
      setShowErrorMessage(false);
      closeEditor();
      dispatch(
        addNotification(
          `✅ ${t('content.notifications.updateIdea.successMessage')}`
        )
      );
    }
  }, [updateIdeaLoading]);

  const saveIdea = useCallback(() => {
    // Serialize the editor's state into plain text
    const text = getPlainText(plateEditor);
    const content: IdeaContent = { text, media };
    // If the current idea is an existing idea, update it
    // otherwise create a new idea
    if (currentIdea && isExistingIdea(currentIdea))
      updateIdea({ id: currentIdea.id, content });
    else createIdea(content, { isFromExtension: fromExtension });
  }, [plateEditor, currentIdea, media]);

  const getCurrentContent = useCallback(() => {
    const text = getPlainText(plateEditor);
    const content: IdeaContent = { text, media };

    const idea: NewIdea = { content };
    if (currentIdea && isExistingIdea(currentIdea)) {
      idea.id = currentIdea.id;
      return idea as Idea;
    }
    return idea;
  }, [plateEditor, currentIdea, media]);

  return useMemo(
    () => ({
      isOpen,
      isEditMode,
      onOpenChange,
      openEditor,
      closeEditor,
      currentIdea,
      saveIdea,
      getCurrentContent,
      showErrorMessage,
      errorCode,
      plateEditor,
    }),
    [
      isOpen,
      isEditMode,
      onOpenChange,
      openEditor,
      closeEditor,
      currentIdea,
      saveIdea,
      getCurrentContent,
      showErrorMessage,
      errorCode,
      plateEditor,
    ]
  );
};
